import type { RaspadinhaContemplatedPrizeImage } from '@/types/raspadinha-campaign'

const MAX_CONTEMPLATIONS_LIMIT = 1_000_000

function buildContemplatedPrizeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `contemplated-prize-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function createEmptyContemplatedPrizeImage(): RaspadinhaContemplatedPrizeImage {
  return {
    id: buildContemplatedPrizeId(),
    title: '',
    image_url: '',
    max_contemplations: 1,
  }
}

export function ensureContemplatedPrizeImages(
  prizes: RaspadinhaContemplatedPrizeImage[] | undefined
): RaspadinhaContemplatedPrizeImage[] {
  return (prizes ?? []).map((prize) => ({
    ...prize,
    title: prize.title ?? '',
    image_url: prize.image_url ?? '',
    max_contemplations: normalizeMaxContemplations(prize.max_contemplations),
  }))
}

export function normalizeMaxContemplations(value: number): number {
  if (!Number.isFinite(value) || value < 1) {
    return 1
  }
  return Math.min(Math.floor(value), MAX_CONTEMPLATIONS_LIMIT)
}

export function addContemplatedPrizeImage(
  prizes: RaspadinhaContemplatedPrizeImage[]
): RaspadinhaContemplatedPrizeImage[] {
  return [...prizes, createEmptyContemplatedPrizeImage()]
}

export function updateContemplatedPrizeImage(
  prizes: RaspadinhaContemplatedPrizeImage[],
  prizeId: string,
  patch: Partial<Omit<RaspadinhaContemplatedPrizeImage, 'id'>>
): RaspadinhaContemplatedPrizeImage[] {
  return prizes.map((prize) => {
    if (prize.id !== prizeId) {
      return prize
    }

    return {
      ...prize,
      ...patch,
      max_contemplations:
        patch.max_contemplations !== undefined
          ? normalizeMaxContemplations(patch.max_contemplations)
          : prize.max_contemplations,
    }
  })
}

export function removeContemplatedPrizeImage(
  prizes: RaspadinhaContemplatedPrizeImage[],
  prizeId: string
): RaspadinhaContemplatedPrizeImage[] {
  return prizes.filter((prize) => prize.id !== prizeId)
}

export function isContemplatedPrizeDraft(prize: RaspadinhaContemplatedPrizeImage): boolean {
  return !prize.title.trim() && !prize.image_url.trim()
}

export function pruneContemplatedPrizeDrafts(
  prizes: RaspadinhaContemplatedPrizeImage[]
): RaspadinhaContemplatedPrizeImage[] {
  return prizes.filter((prize) => !isContemplatedPrizeDraft(prize))
}

export function findContemplatedPrizeById(
  prizes: RaspadinhaContemplatedPrizeImage[],
  prizeId: string
): RaspadinhaContemplatedPrizeImage | null {
  if (!prizeId.trim()) return null
  return prizes.find((prize) => prize.id === prizeId) ?? null
}

/** Prêmios com título informado — opções válidas para vincular a um horário. */
export function listSelectableContemplatedPrizes(
  prizes: RaspadinhaContemplatedPrizeImage[]
): RaspadinhaContemplatedPrizeImage[] {
  return ensureContemplatedPrizeImages(prizes).filter((prize) => prize.title.trim().length > 0)
}

export function getContemplatedPrizeTitle(
  prizes: RaspadinhaContemplatedPrizeImage[],
  prizeId: string
): string {
  const prize = findContemplatedPrizeById(prizes, prizeId)
  return prize?.title.trim() ?? ''
}

/** Sorteia IDs de prêmio com reposição — um prêmio por horário gerado. */
export function pickRandomContemplatedPrizeIds(
  prizeIds: readonly string[],
  count: number,
  random: () => number = Math.random
): string[] {
  const availableIds = prizeIds.map((id) => id.trim()).filter(Boolean)
  if (availableIds.length === 0 || count < 1) {
    return []
  }

  return Array.from({ length: count }, () => {
    const index = Math.floor(random() * availableIds.length)
    return availableIds[index]!
  })
}

/** Opções do select de prêmio por horário, preservando seleção órfã quando necessário. */
export function buildContemplatedPrizeSelectOptions(
  prizes: RaspadinhaContemplatedPrizeImage[],
  selectedPrizeId: string
): RaspadinhaContemplatedPrizeImage[] {
  const selectable = listSelectableContemplatedPrizes(prizes)
  if (!selectedPrizeId || selectable.some((prize) => prize.id === selectedPrizeId)) {
    return selectable
  }

  const selected = findContemplatedPrizeById(prizes, selectedPrizeId)
  if (selected) {
    return [...selectable, selected]
  }

  return [
    ...selectable,
    {
      id: selectedPrizeId,
      title: '(Prêmio indisponível)',
      image_url: '',
      max_contemplations: 1,
    },
  ]
}

export function validateContemplatedPrizeImages(
  prizes: RaspadinhaContemplatedPrizeImage[]
): string | null {
  for (const [index, prize] of prizes.entries()) {
    if (isContemplatedPrizeDraft(prize)) {
      continue
    }

    const position = index + 1
    const title = prize.title.trim()

    if (!title) {
      return `Informe o título do prêmio contemplado #${position}.`
    }

    if (!prize.image_url.trim()) {
      return `Envie a imagem do prêmio contemplado "${title}".`
    }

    if (prize.max_contemplations < 1) {
      return `O limite de contemplações do prêmio "${title}" deve ser no mínimo 1.`
    }
  }

  return null
}
